import { useState, useCallback } from "react";
import { notification } from "antd";
import { calculateProviderLowDp } from "../../api/CalculatorApi";
import type {
  LowDpCalculationPayload,
  LowDpCalculationResponse,
  MonthlyWithAmount,
} from "../../helpers/interfaces/CalculatorInterfaces";

export const useProviderCalculation = () => {
  const [calculationResult, setCalculationResult] =
    useState<LowDpCalculationResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedTermIndex, setSelectedTermIndex] = useState<number>(0);
  const [isDirty, setIsDirty] = useState(false);

  /**
   * Trigger the low-dp calculation API.
   */
  const calculate = useCallback(
    async (payload: LowDpCalculationPayload) => {
      setLoading(true);
      setError(null);
      // Keep old calculationResult visible while loading
      setSelectedTermIndex(0);

      try {
        const response = await calculateProviderLowDp(payload);

        if (response?.status === 200 || response?.status === 201) {
          const data: LowDpCalculationResponse = response.data;
          setCalculationResult(data);
          setIsDirty(false);
          return data;
        } else {
          const errMsg =
            response?.data?.message ||
            response?.data?.error ||
            "Calculation failed. Please check your inputs.";
          setError(errMsg);
          notification.error({ message: errMsg, duration: 4 });
          return null;
        }
      } catch (err: any) {
        const errMsg = err?.message || "Network error during calculation.";
        setError(errMsg);
        notification.error({ message: errMsg, duration: 4 });
        return null;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  /**
   * Reset all calculation state.
   */
  const reset = useCallback(() => {
    setIsDirty(true);
  }, []);

  // ── Derived helpers ──

  /** All monthly term results from the API */
  const monthlyTerms: MonthlyWithAmount[] =
    calculationResult?.monthly_with_amount || [];

  /** The currently-selected monthly term (for partner view navigation) */
  const selectedTerm: MonthlyWithAmount | null =
    monthlyTerms[selectedTermIndex] || null;

  return {
    // State
    calculationResult,
    loading,
    error,
    selectedTermIndex,

    // Derived
    monthlyTerms,
    selectedTerm,
    isDirty,

    // Actions
    calculate,
    reset,
    setSelectedTermIndex,
  };
};
